// ./src/components/features/ServicePoints/servicePointRows.ts
//
// ROADMAP #169 — the editor's rows for a list of service points, and the whole list to save. Relative imports only, so
// the guards run it (`check-service-points`).
import type { ServicePoint, ServicePointInput } from "../../../utils/api/ServicePointService";
import { SERVICE_POINT_KIND, storedActivity } from "../../../utils/servicePoints";

export interface ServicePointRow {
  /** Stable local key for React lists (NOT sent). */
  key: string;
  /** The saved point — absent for a new row. */
  id?: string;
  name: string;
  kind: string;
  /** #176 — what is done here; null = the kind's default (the editor shows the kind's, and stores null for it). */
  activity: string | null;
  /** Kept from load (no field here yet) so a save never empties it. */
  notes: string | null;
  /** #68 — what guests are told: a number to call, how to find it, where it is, a picture. */
  phone: string;
  guestNote: string;
  /** What the pin is (the map drawer fills it; the host may reword it). */
  address: string;
  latitude: number | null;
  longitude: number | null;
  /** The picture: the uploaded file to send, and the url to preview. */
  photo: { uploadedFileId?: string | null; url?: string | null } | null;
  venueAddressId: string | null;
}

let keySeq = 0;
const nextKey = (): string => {
  keySeq += 1;

  return `point-${keySeq}`;
};

export const createEmptyServicePoint = (kind: string = SERVICE_POINT_KIND.ENTRANCE): ServicePointRow => ({
  key: nextKey(),
  name: "",
  kind,
  activity: null,
  notes: null,
  phone: "",
  guestNote: "",
  address: "",
  latitude: null,
  longitude: null,
  photo: null,
  venueAddressId: null,
});

/**
 * #176 — a row given a new kind: one that followed its kind keeps following it (null); a choice of its own stays, unless
 * it is now the new kind's own (null again).
 */
export const rowWithKind = (row: ServicePointRow, kind: string): ServicePointRow => ({
  ...row,
  kind,
  activity: row.activity ? storedActivity(kind, row.activity) : null,
});

/** #176 — a row given an activity: the kind's own is stored as null (the point keeps following its kind). */
export const rowWithActivity = (row: ServicePointRow, activity: string | null): ServicePointRow => ({
  ...row,
  activity: storedActivity(row.kind, activity),
});

/** Editor rows from saved points (in their order). */
export const servicePointRowsFrom = (points: ServicePoint[] | null | undefined): ServicePointRow[] =>
  (points ?? []).map((p) => ({
    key: nextKey(),
    id: p.id,
    name: p.name ?? "",
    kind: p.kind || SERVICE_POINT_KIND.ENTRANCE,
    activity: p.activity ?? null,
    notes: p.notes ?? null,
    phone: p.phone ?? "",
    guestNote: p.guestNote ?? "",
    address: p.address ?? "",
    latitude: p.latitude ?? null,
    longitude: p.longitude ?? null,
    photo: p.photoUploadedFileId ? { uploadedFileId: p.photoUploadedFileId, url: p.photoUploadedFile?.thumbnailUrl || p.photoUploadedFile?.fileUrl || null } : null,
    venueAddressId: p.venueAddressId ?? null,
  }));

/** The whole list to save. A row left with no name is SENT — the server says "Name the point" at it. */
export const servicePointRowsToInput = (rows: ServicePointRow[], withAddress: boolean): ServicePointInput[] =>
  rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    name: r.name.trim(),
    kind: r.kind,
    // #176 — the kind's own activity is sent as null, so the point keeps following its kind.
    activity: storedActivity(r.kind, r.activity),
    notes: r.notes,
    // #68 — sent always (an empty value clears it on the server).
    phone: r.phone.trim() || null,
    guestNote: r.guestNote.trim() || null,
    address: r.address.trim() || null,
    latitude: r.latitude,
    longitude: r.longitude,
    photoUploadedFileId: r.photo?.uploadedFileId ?? null,
    ...(withAddress ? { venueAddressId: r.venueAddressId } : {}),
  }));

